import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import DashboardLayout from '../../../../shared/components/dashboard-layout/dashboard-layout';
import ExternalAccessApi from '../../../../api/external-access-api';
import apiEndpointsData from '../../data/api-endpoints.json';
import './developer-access-page.scss';

const DeveloperAccessPage = () => {
  const { userInfo } = useSelector((state) => state.userStore);
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeEndpoint, setActiveEndpoint] = useState('ping');
  const [endpoints] = useState(apiEndpointsData.endpoints);
  
  // Webhook state
  const [webhook, setWebhook] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookError, setWebhookError] = useState(null);
  const [isCreatingWebhook, setIsCreatingWebhook] = useState(false);
  const [isEditingWebhook, setIsEditingWebhook] = useState(false);
  const [webhookForm, setWebhookForm] = useState({
    webhookUrl: '',
    healthCheckUrl: ''
  });
  const [webhookSaving, setWebhookSaving] = useState(false);

  useEffect(() => {
    fetchApiKey();
    fetchWebhook();
  }, []);

  const fetchApiKey = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ExternalAccessApi.private.getApiKey();
      if (response?.data?.data?.apiKey) {
        setApiKey(response.data.data.apiKey);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      // If it's a 404 error, it just means no API key exists yet, so don't show an error
      if (err.response && err.response.status === 404) {
        console.log('No API key found for this organization');
      } else {
        setError('Failed to fetch API key. Please try again.');
        console.error('Error fetching API key:', err);
      }
    }
  };

  const generateApiKey = async () => {
    try {
      setGenerating(true);
      setError(null);
      const response = await ExternalAccessApi.private.createApiKey();
      if (response?.data?.data?.apiKey) {
        setApiKey(response.data.data.apiKey);
      }
      setGenerating(false);
    } catch (err) {
      setGenerating(false);
      setError('Failed to generate API key. Please try again.');
      console.error('Error generating API key:', err);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const fetchWebhook = async () => {
    try {
      setWebhookLoading(true);
      setWebhookError(null);
      const response = await ExternalAccessApi.private.getWebhook();
      if (response?.data?.data?.webhookUrl) {
        setWebhook(response.data.data);
        setWebhookForm({
          webhookUrl: response.data.data.webhookUrl || '',
          healthCheckUrl: response.data.data.healthCheckUrl || ''
        });
      }
      setWebhookLoading(false);
    } catch (err) {
      setWebhookLoading(false);
      // If it's a 404 error, it just means no webhook exists yet, so don't show an error
      if (err.response && err.response.status === 404) {
        console.log('No webhook found for this organization');
      } else {
        setWebhookError('Failed to fetch webhook configuration. Please try again.');
        console.error('Error fetching webhook:', err);
      }
    }
  };

  const handleWebhookFormChange = (field, value) => {
    setWebhookForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const createWebhook = async () => {
    try {
      setWebhookSaving(true);
      setWebhookError(null);
      
      const payload = {
        webhookUrl: webhookForm.webhookUrl?.trim(),
        ...(webhookForm.healthCheckUrl && webhookForm.healthCheckUrl.trim()
          ? { healthCheckUrl: webhookForm.healthCheckUrl.trim() }
          : {}),
      };

      const response = await ExternalAccessApi.private.createWebhook(payload);
      
      if (response?.data?.data) {
        setWebhook(response.data.data);
        setIsCreatingWebhook(false);
        setWebhookForm({
          webhookUrl: '',
          healthCheckUrl: ''
        });
      }
      setWebhookSaving(false);
    } catch (err) {
      setWebhookSaving(false);
      setWebhookError('Failed to create webhook configuration. Please try again.');
      console.error('Error creating webhook:', err);
    }
  };

  const updateWebhook = async () => {
    try {
      setWebhookSaving(true);
      setWebhookError(null);
      
      const payload = {
        ...(webhookForm.webhookUrl && webhookForm.webhookUrl.trim()
          ? { webhookUrl: webhookForm.webhookUrl.trim() }
          : {}),
        ...(webhookForm.healthCheckUrl && webhookForm.healthCheckUrl.trim()
          ? { healthCheckUrl: webhookForm.healthCheckUrl.trim() }
          : {}),
      };

      const response = await ExternalAccessApi.private.updateWebhook(webhook?._id, payload);
      
      if (response?.data?.data) {
        setWebhook(response.data.data);
        setIsEditingWebhook(false);
      }
      setWebhookSaving(false);
    } catch (err) {
      setWebhookSaving(false);
      setWebhookError('Failed to update webhook configuration. Please try again.');
      console.error('Error updating webhook:', err);
    }
  };

  const deleteWebhook = async () => {
    if (!window.confirm('Are you sure you want to delete the webhook configuration? This action cannot be undone.')) {
      return;
    }

    try {
      setWebhookSaving(true);
      setWebhookError(null);
      
      await ExternalAccessApi.private.deleteWebhook(webhook?._id);
      
      setWebhook(null);
      setWebhookForm({
        webhookUrl: '',
        healthCheckUrl: ''
      });
      setIsEditingWebhook(false);
      setWebhookSaving(false);
    } catch (err) {
      setWebhookSaving(false);
      setWebhookError('Failed to delete webhook configuration. Please try again.');
      console.error('Error deleting webhook:', err);
    }
  };

  const startEditWebhook = () => {
    setWebhookForm({
      webhookUrl: webhook.webhookUrl || '',
      healthCheckUrl: webhook.healthCheckUrl || ''
    });
    setIsEditingWebhook(true);
    setWebhookError(null);
  };

  const cancelCreateWebhook = () => {
    setWebhookForm({
      webhookUrl: '',
      healthCheckUrl: ''
    });
    setIsCreatingWebhook(false);
    setWebhookError(null);
  };

  const cancelEditWebhook = () => {
    setWebhookForm({
      webhookUrl: '',
      healthCheckUrl: ''
    });
    setIsEditingWebhook(false);
    setWebhookError(null);
  };

  return (
    <div className="developer-access-container">
        <h1 className="mb-3 mt-0">Developer Access</h1>
        {/* <div className="developer-access-hero">
          <div className="hero-content">
            <h1>Developer Access</h1>
            <p>Integrate with RELAi's powerful external APIs</p>
            <div className="hero-badges">
              <span className="badge">REST API</span>
              <span className="badge">Webhooks</span>
              <span className="badge">Notifications</span>
            </div>
          </div>
          <div className="hero-image">
            <img src="/assets/images/dev_console.png" alt="Developer Console" />
          </div>
        </div> */}

        <div className="card api-key-card">
          <div className="card-header p-0">
            <h3>API Keys</h3>
            <p>Use this API key to authenticate your requests to the Jely API.</p>
          </div>
          <div className="card-body px-3">
            {loading ? (
              <div className="loading-indicator">Loading API key...</div>
            ) : (
              <>
                {apiKey ? (
                  <div className="api-key-display">
                    <div className="api-key-value">
                      <div className="api-key-text">{apiKey}</div>
                      <button 
                        className="bouncy-button small" 
                        onClick={copyToClipboard}
                        disabled={copied}
                      >
                        <span>{copied ? '✓ Copied!' : 'Copy Key'}</span>
                      </button>
                    </div>
                    <div className="alert alert-info">
                      <i className="info-icon me-3">ℹ️</i>
                      <span>This is your organization's API key. Keep it secure and don't share it publicly.</span>
                    </div>
                  </div>
                ) : (
                  <div className="no-api-key">
                    <p>No API key found for your organization.</p>
                    <button 
                      className="generate-button" 
                      onClick={generateApiKey}
                      disabled={generating}
                    >
                      {generating ? 'Generating...' : 'Generate API Key'}
                    </button>
                  </div>
                )}
              </>
            )}
            {error && <div className="error-message">{error}</div>}
          </div>
        </div>

        <div className="card webhook-card">
          <div className="card-header p-0">
            <h3>Webhook Configuration</h3>
            <p>Configure a webhook endpoint to receive notifications when new messages arrive.</p>
          </div>
          <div className="card-body px-3">
            {webhookLoading ? (
              <div className="loading-indicator">Loading webhook configuration...</div>
            ) : (
              <>
                {webhook && !isEditingWebhook ? (
                  <div className="webhook-display">
                    <div className="webhook-info">
                      <div className="webhook-field">
                        <label>Webhook URL:</label>
                        <div className="webhook-value">{webhook.webhookUrl}</div>
                      </div>
                      {webhook.healthCheckUrl && (
                        <div className="webhook-field">
                          <label>Health Check URL:</label>
                          <div className="webhook-value">{webhook.healthCheckUrl}</div>
                        </div>
                      )}
                      <div className="webhook-status">
                        <span className={`status-badge ${webhook.status === 'active' ? 'active' : 'inactive'}`}>
                          {webhook.status}
                        </span>
                      </div>
                    </div>
                    <div className="webhook-actions">
                      <button 
                        className="bouncy-button small" 
                        onClick={startEditWebhook}
                        disabled={webhookSaving}
                      >
                        Edit Webhook
                      </button>
                      <button 
                        className="bouncy-button small danger" 
                        onClick={deleteWebhook}
                        disabled={webhookSaving}
                      >
                        {webhookSaving ? 'Deleting...' : 'Delete Webhook'}
                      </button>
                    </div>
                  </div>
                ) : (webhook && isEditingWebhook) || isCreatingWebhook ? (
                  <div className="webhook-form">
                    <div className="form-group">
                      <label htmlFor="webhookUrl">Webhook URL *</label>
                      <input
                        type="url"
                        id="webhookUrl"
                        value={webhookForm.webhookUrl}
                        onChange={(e) => handleWebhookFormChange('webhookUrl', e.target.value)}
                        placeholder="https://your-domain.com/webhook"
                        required
                      />
                      <small>This URL will receive POST requests with message data when new messages arrive.</small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="healthCheckUrl">Health Check URL</label>
                      <input
                        type="url"
                        id="healthCheckUrl"
                        value={webhookForm.healthCheckUrl}
                        onChange={(e) => handleWebhookFormChange('healthCheckUrl', e.target.value)}
                        placeholder="https://your-domain.com/health"
                      />
                      <small>Optional: URL for health checks to ensure your webhook is operational.</small>
                    </div>
                    <div className="form-actions">
                      <button 
                        className="generate-button" 
                        onClick={isEditingWebhook ? updateWebhook : createWebhook}
                        disabled={webhookSaving || !webhookForm.webhookUrl.trim()}
                      >
                        {webhookSaving ? 
                          (isEditingWebhook ? 'Updating...' : 'Creating...') : 
                          (isEditingWebhook ? 'Update Webhook' : 'Create Webhook')
                        }
                      </button>
                      <button 
                        className="bouncy-button small" 
                        onClick={isEditingWebhook ? cancelEditWebhook : cancelCreateWebhook}
                        disabled={webhookSaving}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="no-webhook">
                    <p>No webhook configured for your organization.</p>
                    <button 
                      className="generate-button" 
                      onClick={() => setIsCreatingWebhook(true)}
                    >
                      Configure Webhook
                    </button>
                  </div>
                )}
              </>
            )}
            {webhookError && <div className="error-message">{webhookError}</div>}
            
            <div className="alert alert-info mt-3">
              <i className="info-icon me-3">ℹ️</i>
              <div>
                <strong>Webhook Payload:</strong> Your webhook will receive POST requests with the following structure:
                <pre className="code-block mt-2">
                  <code>
{`{
  "_id": "6730c9...",
  "direction": "outgoing",
  "platformOn": "whatsapp",
  "msgType": "notification",
  "message": "Hello from Relai",
  "attachments": [],
  "metadata": { "templateName": "welcome_v1" },
  "status": "sent",
  "createdAt": "2025-08-26T18:48:00.000Z",
  "raw": { "provider": "twilio", "sid": "SM..." }
}`}
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </div>

        <div className="card card-sub-section">
          <div className="card-header p-0">
            <h3>API Documentation</h3>
            <p>Explore the available endpoints and learn how to integrate with Jely.</p>
          </div>
          <div className="card-body px-3">
            <div className="card-subheader">
              <h4>Base URL</h4>
              <p>All API endpoints are relative to the following base URL:</p>
              <pre className="code-block">
                <code>
                  {process.env.REACT_APP_API_BASE_URL.replace('api/v1', 'external/v1')}
                </code>
              </pre>
            </div>
            
            <div className="card-subheader">
              <h4>Authentication</h4>
              <p>Include your API key in the request header:</p>
              <pre className="code-block">
                <code>
                  x-api-key: YOUR_API_KEY
                </code>
              </pre>
            </div>
            
            <div className="api-docs-container">
              <div className="api-sidebar">
                <h4>Available Endpoints</h4>
                <ul className="api-nav">
                  {endpoints.map(endpoint => (
                    <li 
                      key={endpoint.id}
                      className={activeEndpoint === endpoint.id ? 'active' : ''}
                      onClick={() => setActiveEndpoint(endpoint.id)}
                    >
                      <span className="endpoint-method">{endpoint.method}</span>
                      <span className="endpoint-path">{endpoint.path.split('/').slice(-1)[0]}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="api-content">
                {endpoints.map(endpoint => (
                  activeEndpoint === endpoint.id && (
                    <div className="api-endpoint-details" key={endpoint.id}>
                      <div className="endpoint-header">
                        <span className={`endpoint-method ${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
                        <span className="endpoint-full-path">{endpoint.path}</span>
                      </div>
                      <div className="endpoint-description">
                        <p>{endpoint.description}</p>
                      </div>
                      
                      <div className="endpoint-section">
                        <h5>Request</h5>
                        <pre className="code-block">
                          <code>
                            {`${endpoint.method} ${endpoint.path}

Headers:
${Object.entries(endpoint.request.headers).map(([key, value]) => `${key}: ${value}`).join('\n')}`}
                            {endpoint.request.body && `

Body:
${JSON.stringify(endpoint.request.body, null, 2)}`}
                          </code>
                        </pre>
                      </div>
                      
                      <div className="endpoint-section">
                        <h5>Response</h5>
                        <pre className="code-block">
                          <code>
                            {JSON.stringify(endpoint.response.success.content, null, 2)}
                          </code>
                        </pre>
                      </div>
                      
                      {endpoint.parameters && endpoint.parameters.length > 0 && (
                        <div className="endpoint-section">
                          <h5>Parameters</h5>
                          <table className="parameters">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Required</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {endpoint.parameters.map(param => (
                                <tr key={param.name}>
                                  <td>{param.name}</td>
                                  <td>{param.type}</td>
                                  <td>{param.required ? 'Yes' : 'No'}</td>
                                  <td>{param.description}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      
                      <div className="endpoint-section">
                        <h5>Status Codes</h5>
                        <table className="status-codes">
                          <tbody>
                            {endpoint.statusCodes.map(status => (
                              <tr key={status.code}>
                                <td>{status.code}</td>
                                <td>{status.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default DeveloperAccessPage;
